/**
 * Filter state management hook for DataList.
 *
 * Manages the current filter values, provides setters for individual
 * filter changes and a bulk reset, and computes the `filterParams`
 * record used by the data-fetching layer to build API query parameters.
 *
 * Filter values are initialised from each `FilterConfig.defaultValue`.
 * The computed `filterParams` uses `filter.paramName ?? filter.id` as
 * the query-parameter key and omits entries whose value is null,
 * undefined, or an empty string.
 *
 * @see Plan section 3.5 for hook design.
 * @see Spec section 9.2 for filtering requirements.
 */

import { useState, useMemo, useCallback } from 'react';

import type { FilterConfig } from '../types';

// ---------------------------------------------------------------------------
// Return Type
// ---------------------------------------------------------------------------

/**
 * Public return type of useListFilters.
 */
export interface UseListFiltersReturn {
  /** Current filter values keyed by filter id */
  filterValues: Record<string, unknown>;

  /** Update the value for a single filter */
  setFilterValue: (filterId: string, value: unknown) => void;

  /** Reset all filters to their default values */
  resetFilters: () => void;

  /**
   * Computed query-parameter record derived from current filter values.
   *
   * - Uses `filter.paramName ?? filter.id` as the key.
   * - Omits entries whose value is `null`, `undefined`, or `''`.
   */
  filterParams: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Builds the initial filter values record from an array of FilterConfig.
 * Only filters that declare a `defaultValue` are included.
 */
function buildInitialValues(
  config: FilterConfig[] | undefined,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  config?.forEach((filter) => {
    if (filter.defaultValue !== undefined) {
      values[filter.id] = filter.defaultValue;
    }
  });

  return values;
}

/**
 * Returns `true` when the value should be considered "empty" and
 * therefore excluded from the computed `filterParams`.
 */
function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages filter state for a DataList instance.
 *
 * @param config - Array of filter definitions from `toolbar.filters`.
 *                 May be `undefined` when the list has no filters.
 * @returns Filter state, mutators, and computed API params.
 *
 * @example
 * ```ts
 * const { filterValues, setFilterValue, resetFilters, filterParams } =
 *   useListFilters(config.toolbar?.filters);
 * ```
 */
export function useListFilters(
  config: FilterConfig[] | undefined,
): UseListFiltersReturn {
  // ---- Initial values (memoised from config) ----

  const initialValues = useMemo(
    () => buildInitialValues(config),
    [config],
  );

  // ---- State ----

  const [filterValues, setFilterValues] = useState<Record<string, unknown>>(
    initialValues,
  );

  // ---- Mutators ----

  /**
   * Update the value for a single filter by id.
   */
  const setFilterValue = useCallback(
    (filterId: string, value: unknown): void => {
      setFilterValues((prev) => ({ ...prev, [filterId]: value }));
    },
    [],
  );

  /**
   * Reset all filter values back to the initial defaults derived from config.
   */
  const resetFilters = useCallback((): void => {
    setFilterValues(initialValues);
  }, [initialValues]);

  // ---- Computed: filterParams ----

  /**
   * Builds the query-parameter record from current filter values.
   *
   * For each filter that has a non-empty value, the key is determined by
   * `filter.paramName` (falling back to `filter.id`) and the value is the
   * current filter value.
   */
  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = {};

    if (!config) {
      return params;
    }

    for (const filter of config) {
      const value = filterValues[filter.id];

      if (isEmptyValue(value)) {
        continue;
      }

      const paramKey = filter.paramName ?? filter.id;
      params[paramKey] = value;
    }

    return params;
  }, [config, filterValues]);

  // ---- Return ----

  return {
    filterValues,
    setFilterValue,
    resetFilters,
    filterParams,
  };
}
